/**
 * FeatureToolbar — the content-pane toolbar: search box, mode selector, and
 * Help / Clear-Whitelist actions. Extracted from FeatureManagerWindow;
 * behavior-preserving (same controls, same handlers, same disabled rules).
 */
import React from 'react';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
} from '../../theme';
import { Toolbar, Button } from '../../primitives';
import { FeatureMode } from '../../../../core/features';

export interface FeatureToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  currentMode: FeatureMode;
  onModeChange: (mode: FeatureMode) => void;
  onToggleHelp: () => void;
  onClearWhitelist: () => void;
}

const toolbarRowStyle: React.CSSProperties = {
  padding: `${SPACE.xl}px ${SPACE.xxl}px`,
  borderBottom: `1px solid ${COLORS.borderSubtle}`,
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 200,
  padding: `${SPACE.md}px ${SPACE.lg + SPACE.xxs}px`,
  borderRadius: RADII.lg,
  border: `1px solid ${COLORS.borderSubtle}`,
  background: COLORS.surfaceSunken,
  color: COLORS.textPrimary,
  fontSize: FONT_SIZE.md,
  outline: 'none',
};

const modeSelectorStyle: React.CSSProperties = {
  padding: `${SPACE.md}px ${SPACE.lg}px`,
  borderRadius: RADII.lg,
  border: `1px solid ${COLORS.borderSubtle}`,
  background: COLORS.surfaceSunken,
  color: COLORS.textPrimary,
  fontSize: FONT_SIZE.md,
  cursor: 'pointer',
};

export const FeatureToolbar: React.FC<FeatureToolbarProps> = ({
  searchQuery,
  onSearchChange,
  currentMode,
  onModeChange,
  onToggleHelp,
  onClearWhitelist,
}) => {
  return (
    <div style={toolbarRowStyle}>
      <Toolbar gap="lg">
        <input
          type="text"
          placeholder="Search features..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={searchInputStyle}
        />

        <select
          value={currentMode}
          onChange={(e) => onModeChange(e.target.value as FeatureMode)}
          style={modeSelectorStyle}
        >
          <option value={FeatureMode.NORMAL}>✓ Normal Mode</option>
          <option value={FeatureMode.WHITELIST}>🧪 Whitelist Mode</option>
          <option value={FeatureMode.DISABLED}>⊘ Disabled</option>
        </select>

        <Button variant="outline" size="sm" iconLeft="❓" onClick={onToggleHelp}>
          Help
        </Button>

        <Button
          variant="danger"
          size="sm"
          iconLeft="🗑"
          onClick={onClearWhitelist}
          disabled={currentMode !== FeatureMode.WHITELIST}
        >
          Clear Whitelist
        </Button>
      </Toolbar>
    </div>
  );
};
